import classnames from "classnames";
import {
  autorun,
  computed,
  observable,
  reaction,
  makeObservable,
  action,
} from "mobx";
import { observer } from "mobx-react";
import { Component, createRef } from "react";

import {
  Notification,
  NotificationsStore,
} from "nvent-web/stores/Notifications";

import { Notification as NotificationComponent } from "./Notification";
import style from "./Notifications.module.scss";
import compose from "~/nvent-web/utils/compose";

export interface NotificationsProps {
  notifications: NotificationsStore;
}

class AnimatedNotification {
  notification: Notification;
  animationClassName?: "animateIn" | "animateOut";
  ref = createRef<HTMLLIElement>();

  get key() {
    return this.notification.key;
  }

  get isVisible() {
    return Boolean(this.notification.isVisible || this.animationClassName);
  }

  constructor(notification: Notification) {
    this.notification = notification;

    makeObservable(this, {
      notification: observable,
      animationClassName: observable,
      key: computed,
      isVisible: computed,
      setAnimationClassName: action.bound,
    });

    reaction(
      () => notification.isVisible,
      (isVisible) => {
        if (!isVisible && this.ref.current) {
          this.ref.current.style.height = `${this.ref.current.offsetHeight}px`;
        }

        this.setAnimationClassName(isVisible ? "animateIn" : "animateOut");
      },
      { fireImmediately: true }
    );
  }

  setAnimationClassName(value: typeof this.animationClassName) {
    this.animationClassName = value;
  }

  animationEnded = () => {
    this.setAnimationClassName(undefined);
  };
}

class Notifications extends Component<NotificationsProps> {
  private animationsMap = observable.map<number, AnimatedNotification>();
  private notificationsStore: NotificationsStore;

  get animations() {
    return this.notificationsStore.all.map(({ key }) =>
      this.animationsMap.get(key)
    );
  }

  get visibleAnimations() {
    return this.animations.filter(
      (animation): animation is AnimatedNotification =>
        Boolean(animation?.isVisible)
    );
  }

  constructor(props: NotificationsProps) {
    super(props);

    // Copy props into local observable
    this.notificationsStore = props.notifications;

    makeObservable(this, {
      animations: computed,
      visibleAnimations: computed,
    });

    autorun(() => {
      this.notificationsStore.all.forEach((notification) => {
        if (!this.animationsMap.has(notification.key)) {
          this.animationsMap.set(
            notification.key,
            new AnimatedNotification(notification)
          );
        }
      });
    });
  }

  render() {
    return (
      <div className={style.container}>
        <ul className={style.list}>
          {this.visibleAnimations.map((animation) => (
            <li
              key={animation.key}
              ref={animation.ref}
              className={classnames(
                style.listItem,
                animation.animationClassName &&
                  style[animation.animationClassName]
              )}
              onAnimationEnd={animation.animationEnded}
            >
              <NotificationComponent notification={animation.notification} />
            </li>
          ))}
        </ul>
      </div>
    );
  }
}

const NotificationsComponent =
  compose<NotificationsProps>(observer)(Notifications);

NotificationsComponent.displayName = "Notifications";

export default NotificationsComponent;
